"use client";
import React, { useEffect, useRef, useState } from "react";
import s from "../../styles/pages/about/About.module.scss";
import AboutBanner from "./AboutBanner";
import ServicesGrid from "./components/ServicesGrid";
import Pipeline3D from "./components/Pipeline3D";
import FeaturedArticleEndcap from "./components/FeaturedArticleEndcap";
import { articles as ARTICLES } from "../../data/articlesIndex"; // adjust path
import { useContact } from "../../components/Contacts/ContactModals";
import { createFormspreeSubmitter } from "../../components/Contacts/contactSubmitters";
import Activity from "./components/Activity";
// ---------- Content data ----------

export default function AboutPage(){
  // theme
  const prefersLight = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches;
  const [light, setLight] = useState(() => {
    if (typeof window === "undefined") return prefersLight;
    return localStorage.getItem("theme") === "light" || (!localStorage.getItem("theme") && prefersLight);
  });
  useEffect(() => { if (typeof window !== "undefined") localStorage.setItem("theme", light ? "light" : "dark"); }, [light]);

  const mostRecentArticle = React.useMemo(() => {
  if (!ARTICLES?.length) return null;

  return [...ARTICLES]
    .filter((a) => a?.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
}, []);

// Contact
  const submitter = createFormspreeSubmitter("https://formspree.io/f/mojjblba");
  const { openCalendly, Modal } = useContact(submitter);

  // ui refs
  const progressRef = useRef(null);
  const heroRef = useRef(null);
  const slidesRef = useRef(null);
  const metricsRef = useRef(null);
  const [showCTA, setShowCTA] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // scroll progress
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = Math.max(0, Math.min(1, (window.scrollY || 0) / Math.max(1, h)));
      if (progressRef.current) progressRef.current.style.width = pct * 100 + "%";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // spotlight
  useEffect(() => {
    if (!heroRef.current || reduced) return;
    const spot = heroRef.current.querySelector(`.${s.spot}`);
    const move = (e) => {
      const r = heroRef.current.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      if (spot) { spot.style.setProperty("--mx", x + "%"); spot.style.setProperty("--my", y + "%"); }
    };
    heroRef.current.addEventListener("pointermove", move);
    return () => heroRef.current?.removeEventListener("pointermove", move);
  }, [reduced]);

  // sticky cta
  useEffect(() => {
    if (!heroRef.current) return;
    const io = new IntersectionObserver(([en]) => setShowCTA(!en.isIntersecting), { threshold: 0.4 });
    io.observe(heroRef.current);
    return () => io.disconnect();
  }, []);

  // testimonials auto-advance
  useEffect(() => {
    if (!slidesRef.current || reduced) return;
    let i = 0; const max = slidesRef.current.children.length;
    const id = setInterval(() => { i = (i + 1) % max; slidesRef.current.style.transform = `translateX(-${i * 100}%)`; }, 4800);
    return () => clearInterval(id);
  }, [reduced]);

  // metrics count-up
  useEffect(() => {
    if (!metricsRef.current || reduced) return;
    const els = metricsRef.current.querySelectorAll("[data-count]");
    const started = new WeakSet();
    const start = (el) => {
      if (started.has(el)) return; started.add(el);
      const target = Number(el.getAttribute("data-count"));
      let n = 0; const step = () => { n += Math.max(1, Math.ceil(target / 60)); if (n >= target) { el.textContent = String(target); return; } el.textContent = String(n); requestAnimationFrame(step); }; step();
    };
    const io = new IntersectionObserver((entries) => entries.forEach((en) => en.isIntersecting && start(en.target)), { threshold: 0.6 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [reduced]);

  const copyEmail = async () => {
    const email = "hello@example.com"; // TODO replace
    try { await navigator.clipboard.writeText(email); alert("Email copied: " + email); } catch { alert("Email: " + email); }
  };

  return (
    <div className={`${s.page} ${light ? s.light : ""}`}>
      <div className={s.progress} ref={progressRef} />

      < AboutBanner />

      <main className={s.container}>
        {/* Hero */}
        <section id="about" className={s.hero} ref={heroRef}>
          <div className={s.spot} aria-hidden="true" />
          <p className={s.eyebrow}><span className={s.dot} /> Tech • PUblic Service • Community</p>

          <h1 className={s.title}>About <span className={s.grad}>Me</span>.</h1>
          <div className={s.ledeWrap}>
            <p className={s.lede}>I come from a technical, finance, and business startup background. I’m purpose driven, I travel often for inspiration and cultural exchange, which has strengthen  my appreciation for the beauty of diverse communities. In the past ten years, I've been active in community service, I particularly advocated for human rights with several humanitarian nonprofits, with a particular focus on African regions affected by political instability and repression.</p>

          </div>
          {/* <p className={s.lede}>I come from a technical, finance, and business startup background, and I’m purpose driven. I travel often for inspiration and cultural exchange, which has strengthen  my appreciation for the beauty of diverse communities. I’m also active in community service, I particularly advocated for human rights with several humanitarian nonprofits for years, with a particular focus on African regions affected by political instability and repression.</p> */}
          <div className={s.points}>
            <span className={s.point}>Collaborate</span>
            <span className={s.point}>✔️Explore</span>
            <span className={s.point}>Contact</span>
          </div>
          <div className={s.ctaRow}>
            <a className={`${s.btn} ${s.primary}`} href="#services">See services</a>
            <a className={s.btn} href="#articles">Articles</a>
            <button className={s.btn} onClick={copyEmail}>Copy email</button>
          </div>
        </section>
         {/* Activity */}

         {/* <section  id="services" className={s.section}></section>
          <h2 className={s.kicker}><span className={s.dot} />Activities</h2>
        < Activity />
        <section/> */}


      {/* Services */}

         <section  id="services" className={s.section}></section>
          <h2 className={s.kicker}><span className={s.dot} />Services</h2>
        < ServicesGrid />
        <section/>


 <section  className={s.section}></section>
          <h2 className={s.kicker}><span className={s.dot} />Cyber Threats</h2>

        < Pipeline3D />
        <section/>

 <section id="articles" className={s.section}></section>
          <h2 className={s.kicker}><span className={s.dot} /> Articles </h2>

        < FeaturedArticleEndcap article={mostRecentArticle} />
        <section/>
      </main>

      {/* Sticky CTA */}
      <div className={`${s.ctaBar} ${showCTA ? s.show : ""}`}>
           <button
              type="button"
              className={`${s.btn} ${s.primary}`}
              onClick={openCalendly}
              aria-label="Book a call">
              Book a call
            </button>
           </div>
        <Modal />

      {/* className={`${s.ctaBar} ${showCTA ? s.show : ""}`} */}
      {/* <div className={`${s.ctaBar} ${showCTA ? s.show : ""}`}>Ready to build? <a className={`${s.btn} ${s.primary}`} href="#engage">Book a discovery call</a></div> */}
    </div>
  );
}


// ===== File: AboutPage.module.css =====
